import { useState } from "react";

import { describeApiError } from "../../api/errors";
import { useMigration, useRetryAttachments } from "../../api/hooks";
import { Button, Disclosure } from "../../components";
import { t } from "../../i18n/t";

interface AttachmentsRuntime {
  phase?: string;
  done?: number;
  total?: number;
  uploaded?: number;
  deduped?: number;
  upload_failed?: number;
  orphaned?: number;
  upload_total?: number;
  last_error?: string | null;
  // Round-4 MEDIUM ("FAILED_FETCH is invisible and cannot be retried from the product"): a ref whose
  // TransientError persisted past the fetch cap, mirroring upload_failed's own shape on the fetch side.
  fetch_failed?: number;
}

function runtime(value: unknown): AttachmentsRuntime {
  if (typeof value !== "object" || value === null) return {};
  return value as AttachmentsRuntime;
}

/**
 * Phase 3 spec §9F. `runtime["attachments"]` (the periodic tick's `AttachmentProgress`), promoted to
 * `MigrationOut.attachments` the same way `delta` is promoted (see `DeltaPanel`). The upload phase
 * ("uploading") only starts once the fetch phase's `done >= total` (§9D), so `upload_total`, not `total`,
 * is "M" in the "N of M files attached" copy -- `total` is the fetch pass's own universe and can be 0
 * while the migration has not reached the attachments stage at all.
 */
export function AttachmentsPanel({ migrationId }: { migrationId: string }): JSX.Element | null {
  const migration = useMigration(migrationId);
  const retry = useRetryAttachments(migrationId);
  const [retriedCount, setRetriedCount] = useState<number | null>(null);
  const [retryError, setRetryError] = useState<string | null>(null);
  // Round-6 ALSO: the route refuses to reset FAILED_FETCH rows when the source connection is revoked (a
  // reset it could never honour would only wipe the rows' own failure evidence) -- `retriedCount` alone
  // would otherwise read as "nothing was wrong" (0 reset can also just mean nothing was failed).
  const [fetchRetryBlocked, setFetchRetryBlocked] = useState(false);

  const state = runtime(migration.data?.attachments);
  const phase = state.phase;

  // No attachment phase has run yet (an older migration, or one whose frozen scope never asked for
  // attachments) -- nothing to render, same convention as `FailurePanel` returning null outside its states.
  if (phase === undefined) {
    return null;
  }

  const uploaded = state.uploaded ?? 0;
  const deduped = state.deduped ?? 0;
  const uploadFailed = state.upload_failed ?? 0;
  const orphaned = state.orphaned ?? 0;
  const uploadTotal = state.upload_total ?? 0;
  const fetchFailed = state.fetch_failed ?? 0;
  const attached = uploaded + deduped;

  const needsAttention = uploadFailed > 0 || fetchFailed > 0 || orphaned > 0 || phase === "failed";
  return (
    <Disclosure
      asHeading
      title={t("attachments.title")}
      counts={
        phase === "skipped"
          ? t("attachments.counts_skipped")
          : t("attachments.summary", { attached, total: uploadTotal, failed: uploadFailed })
      }
      open={needsAttention ? true : undefined}
      attention={needsAttention}
      testId="status-attachments-section"
    >
      {phase === "skipped" ? (
        <p data-testid="attachments-skipped">{t("attachments.skipped")}</p>
      ) : (
        <>
          <p data-testid="attachments-summary">
            {t("attachments.summary", { attached, total: uploadTotal, failed: uploadFailed })}
          </p>
          {orphaned > 0 ? (
            <p data-testid="attachments-orphaned">{t("attachments.orphaned", { n: orphaned })}</p>
          ) : null}
          {fetchFailed > 0 ? (
            <p data-testid="attachments-fetch-failed">{t("attachments.fetch_failed", { n: fetchFailed })}</p>
          ) : null}
          {phase === "failed" ? (
            <p data-testid="attachments-phase-failed">{t("attachments.phase_failed")}</p>
          ) : null}
          {state.last_error !== null && state.last_error !== undefined ? (
            <p data-testid="attachments-last-error">
              {t("attachments.last_error", { message: state.last_error })}
            </p>
          ) : null}

          <Button
            // Finding A (BLOCKER): a "failed" phase has zero FAILED_UPLOAD rows (the failing slice
            // rolled back before any upload was attempted), so `uploadFailed === 0` alone used to leave
            // this button disabled forever -- the one product surface that could re-arm the phase was
            // unreachable. Enabled here too; the route re-arms it even with reset==0.
            //
            // Round-4 MEDIUM ("FAILED_FETCH is invisible and cannot be retried from the product"): a run
            // that ends at phase "done" with one or more FAILED_FETCH rows and zero FAILED_UPLOAD rows
            // (the transient-cap outcome) used to leave this button disabled -- `uploadFailed === 0` and
            // `phase !== "failed"` both held, even though `retry-attachments` has real fetch work to
            // reset. `fetchFailed > 0` is the third way in.
            disabled={(uploadFailed === 0 && fetchFailed === 0 && phase !== "failed") || retry.isPending}
            onClick={() => {
              setRetryError(null);
              void retry
                .mutateAsync()
                .then((result) => {
                  setRetriedCount(result.reset);
                  setFetchRetryBlocked(result.fetch_retry_blocked);
                })
                .catch((caught: unknown) => {
                  // Opus review finding M (LOW): an unhandled rejection here used to leave the operator
                  // with no feedback at all -- the button just silently left its loading state, as if
                  // nothing had happened.
                  setRetryError(describeApiError(caught, t("error.unexpected")));
                });
            }}
          >
            {retry.isPending ? t("common.loading") : t("attachments.retry_failed")}
          </Button>
          {retriedCount !== null ? (
            <p data-testid="attachments-retried">{t("attachments.retried", { n: retriedCount })}</p>
          ) : null}
          {fetchRetryBlocked ? (
            <p data-testid="attachments-fetch-retry-blocked">{t("attachments.fetch_retry_blocked")}</p>
          ) : null}
          {retryError !== null ? (
            <p data-testid="attachments-retry-error">
              {t("attachments.retry_error", { message: retryError })}
            </p>
          ) : null}
        </>
      )}
    </Disclosure>
  );
}
